import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Loader2, Plus, X, Hash, MessageSquare, ArrowRight, ArrowLeft, Globe, Sparkles, Pencil } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { CodyIconBadge } from '../components/CodyLogo';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { toast } from 'sonner';

const CARD_CLASS = "p-6 sm:p-8 rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.06)] border-[#E6E8EE] w-full";

const SCAN_STEPS = [
  { key: 'Creating business', label: 'Creating business' },
  { key: 'Setting up subreddits', label: 'Setting up subreddits' },
  { key: 'Scanning Reddit', label: 'Scanning Reddit' },
  { key: 'Scoring leads', label: 'Ranking leads' },
  { key: 'Done', label: 'Done' },
];

export default function Onboard() {
  const [url, setUrl] = useState(sessionStorage.getItem('scanUrl') || '');
  const { businesses, refreshBusinesses, user } = useAuth();
  const navigate = useNavigate();
  const hasBusinesses = businesses.length > 0;

  const plan = user?.plan || 'free';
  const isPaid = plan === 'pro' || plan === 'agency' || user?.role === 'admin';
  const kwLimit = isPaid ? 50 : 5;
  const subLimit = isPaid ? 50 : 5;
  // Pre-fill ~25% of limit for paid users so they personalize; free gets their full 5
  const kwPrefill = isPaid ? 12 : 5;
  const subPrefill = isPaid ? 5 : 5;

  const [step, setStep] = useState('url');
  const [analyzeJobId, setAnalyzeJobId] = useState(null);
  const [analyzeError, setAnalyzeError] = useState(null);

  const [productName, setProductName] = useState('');
  const [productDesc, setProductDesc] = useState('');
  const [problemDomain, setProblemDomain] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [keywords, setKeywords] = useState([]);
  const [subreddits, setSubreddits] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [newSubreddit, setNewSubreddit] = useState('');

  const [scanJobId, setScanJobId] = useState(null);
  const [scanStep, setScanStep] = useState(0);
  const [scanData, setScanData] = useState(null);
  const [scanError, setScanError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const pollRef = useRef(null);
  const autoLoadedRef = useRef(false);
  const completedRef = useRef(false);

  // Auto-load scan results from landing page — runs ONCE
  useEffect(() => {
    if (autoLoadedRef.current) return;
    const stored = sessionStorage.getItem('scanResults');
    const scanUrl = sessionStorage.getItem('scanUrl');
    if (stored && scanUrl) {
      autoLoadedRef.current = true;
      sessionStorage.removeItem('scanResults');
      try {
        const data = JSON.parse(stored);
        if (data.product_name) {
          setUrl(scanUrl);
          setProductName(data.product_name || '');
          setProductDesc(data.product_description || '');
          setProblemDomain(data.problem_domain || '');
          setKeywords((data.keywords || []).slice(0, kwPrefill));
          setSubreddits((data.suggested_subreddits || []).slice(0, subPrefill));
          setStep('review');
        }
      } catch {}
    }
  }, [kwLimit, subLimit]);

  // Poll analyze status
  useEffect(() => {
    if (!analyzeJobId) return;
    const poll = async () => {
      try {
        const status = await api.analyzeStatus(analyzeJobId);
        if (status.status === 'completed' && status.result) {
          clearInterval(pollRef.current);
          setProductName(status.result.product_name || '');
          setProductDesc(status.result.product_description || '');
          setProblemDomain(status.result.problem_domain || '');
          setKeywords((status.result.keywords || []).slice(0, kwPrefill));
          setSubreddits((status.result.suggested_subreddits || []).slice(0, subPrefill));
          setStep('review');
        } else if (status.status === 'failed') {
          clearInterval(pollRef.current);
          setAnalyzeError(status.error || 'Analysis failed');
          setStep('url');
        }
      } catch {}
    };
    poll();
    pollRef.current = setInterval(poll, 2000);
    return () => clearInterval(pollRef.current);
  }, [analyzeJobId, kwLimit, subLimit]);

  // Poll scan status
  useEffect(() => {
    if (!scanJobId) return;
    const poll = async () => {
      try {
        const status = await api.onboardStatus(scanJobId);
        setScanData(status);
        const idx = SCAN_STEPS.findIndex(s => s.key === status.step);
        if (idx >= 0) setScanStep(idx);
        if (status.status === 'completed' && !completedRef.current) {
          completedRef.current = true;
          clearInterval(pollRef.current);
          setScanStep(SCAN_STEPS.length);
          sessionStorage.removeItem('scanUrl');
          toast.success('Business set up!');
          // Refresh businesses then navigate
          try { await refreshBusinesses(); } catch {}
          setTimeout(() => navigate('/dashboard'), 1200);
        } else if (status.status === 'failed') {
          clearInterval(pollRef.current);
          setScanError(status.error || 'Failed');
          setStep('review');
          setConfirming(false);
        }
      } catch {}
    };
    poll();
    pollRef.current = setInterval(poll, 2000);
    return () => clearInterval(pollRef.current);
  }, [scanJobId, navigate]);

  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setAnalyzeError(null);
    setStep('analyzing');
    try {
      const result = await api.analyzeWebsite(url);
      setAnalyzeJobId(result.job_id);
    } catch (err) { setAnalyzeError(err.message); setStep('url'); }
  };

  const handleConfirm = async () => {
    if (confirming) return; // prevent double-click
    if (keywords.length === 0 || subreddits.length === 0) { toast.error('Add at least one keyword and one subreddit'); return; }
    setConfirming(true);
    setScanError(null);
    setStep('scanning');
    setScanStep(0);
    completedRef.current = false;
    try {
      const result = await api.onboardBusiness({ url, product_name: productName, product_description: productDesc, problem_domain: problemDomain, keywords, subreddits });
      setScanJobId(result.job_id);
    } catch (err) {
      setScanError(err.message);
      setStep('review');
      setConfirming(false);
    }
  };

  const addKeyword = () => { const kw = newKeyword.trim(); if (!kw) return; if (keywords.length >= kwLimit) { toast.error(`Plan limit: ${kwLimit} keywords`); return; } if (!keywords.includes(kw)) { setKeywords(prev => [...prev, kw]); } setNewKeyword(''); };
  const removeKeyword = (kw) => setKeywords(prev => prev.filter(k => k !== kw));
  const addSubreddit = () => { const s = newSubreddit.trim().replace(/^r\//, ''); if (!s) return; if (subreddits.length >= subLimit) { toast.error(`Plan limit: ${subLimit} subreddits`); return; } if (!subreddits.includes(s)) { setSubreddits(prev => [...prev, s]); } setNewSubreddit(''); };
  const removeSubreddit = (s) => setSubreddits(prev => prev.filter(x => x !== s));

  const isDone = step === 'scanning' && scanStep >= SCAN_STEPS.length;
  const scanProgress = isDone ? 100 : step === 'scanning' ? Math.min(((scanStep + 1) / SCAN_STEPS.length) * 100, 95) : 0;

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
      <div className="absolute top-0 left-0 right-0 h-[30vh] hero-gradient" />

      <AnimatePresence mode="wait">
        {/* ── URL Entry ── */}
        {step === 'url' && (
          <motion.div key="url" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="relative w-full max-w-2xl">
            <Card className={CARD_CLASS}>
              <div className="flex items-center justify-center mb-6"><CodyIconBadge size={44} /></div>
              <h2 className="text-xl font-bold text-center mb-1" style={{ fontFamily: "'Space Grotesk'" }}>
                {hasBusinesses ? 'Add a new business' : 'Add your first business'}
              </h2>
              <p className="text-sm text-[#5B667A] text-center mb-6">Enter your website URL to get started</p>
              <form onSubmit={handleAnalyze} className="space-y-4">
                <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://yourproduct.com" required className="h-11 rounded-lg" data-testid="onboard-url-input" />
                <Button type="submit" className="w-full h-11 rounded-lg font-semibold text-sm" style={{ background: 'linear-gradient(180deg, #FF6A2A 0%, #FF4500 100%)' }} data-testid="onboard-submit-button">
                  Analyze Website <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>
              {analyzeError && <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{analyzeError}</div>}
              {hasBusinesses && <Button variant="ghost" className="w-full mt-3 text-xs text-[#5B667A]" onClick={() => navigate('/dashboard')}><ArrowLeft className="w-3 h-3 mr-1" /> Back to Dashboard</Button>}
            </Card>
          </motion.div>
        )}

        {/* ── Analyzing ── */}
        {step === 'analyzing' && (
          <motion.div key="analyzing" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="relative w-full max-w-2xl">
            <Card className={CARD_CLASS}>
              <div className="flex items-center justify-center mb-6"><CodyIconBadge size={44} /></div>
              <h2 className="text-xl font-bold text-center mb-1" style={{ fontFamily: "'Space Grotesk'" }}>Analyzing your website</h2>
              <p className="text-sm text-[#5B667A] text-center mb-6">Extracting product info and finding keywords...</p>
              <div className="flex items-center justify-center gap-3 py-4">
                <Loader2 className="w-6 h-6 text-[#FF4500] animate-spin" />
              </div>
              <Progress value={45} className="h-1.5" />
            </Card>
          </motion.div>
        )}

        {/* ── Review ── */}
        {step === 'review' && (
          <motion.div key="review" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="relative w-full max-w-2xl">
            <Card className={CARD_CLASS}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <CodyIconBadge size={36} />
                  <div>
                    <h2 className="text-lg font-bold" style={{ fontFamily: "'Space Grotesk'" }}>Review & customize</h2>
                    <p className="text-[11px] text-[#5B667A]">Edit details, then start scanning Reddit</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="text-xs text-[#5B667A]" onClick={() => { setStep('url'); setAnalyzeJobId(null); }}>
                  <ArrowLeft className="w-3 h-3 mr-1" /> Change URL
                </Button>
              </div>

              <div className="p-3 rounded-lg bg-[#F9FAFB] border border-[#E6E8EE]/60 mb-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Globe className="w-3.5 h-3.5 text-[#FF4500]" />
                    <span className="text-sm font-bold text-[#0B0F17]">{productName}</span>
                  </div>
                  <button onClick={() => setEditingDesc(!editingDesc)} className="text-[10px] text-[#5B667A] hover:text-[#FF4500] flex items-center gap-1">
                    <Pencil className="w-2.5 h-2.5" /> {editingDesc ? 'Done' : 'Edit'}
                  </button>
                </div>
                {editingDesc ? (
                  <Textarea value={productDesc} onChange={(e) => setProductDesc(e.target.value)} rows={2} className="text-xs mt-1 resize-none" placeholder="Describe your product..." />
                ) : (
                  <p className="text-xs text-[#5B667A] leading-relaxed">{productDesc}</p>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5"><Hash className="w-3.5 h-3.5 text-[#FF4500]" /><span className="text-sm font-bold">Keywords</span></div>
                    <span className="text-[10px] text-[#9ca3af]">{keywords.length}/{kwLimit}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {keywords.map((kw, i) => (
                      <span key={i} className="inline-flex items-center gap-0.5 text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#FFF2EA] text-[#FF4500] border border-[#FF4500]/10">
                        {kw}<button onClick={() => removeKeyword(kw)} className="hover:bg-[#FF4500]/10 rounded-full p-0.5 ml-0.5"><X className="w-2 h-2" /></button>
                      </span>
                    ))}
                  </div>
                  {keywords.length < kwLimit && (
                    <div className="flex gap-1.5">
                      <Input placeholder="Add keyword..." value={newKeyword} onChange={(e) => setNewKeyword(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())} className="h-7 text-[11px] rounded-md flex-1" />
                      <Button variant="outline" size="sm" className="h-7 w-7 p-0 rounded-md" onClick={addKeyword}><Plus className="w-3 h-3" /></Button>
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5 text-[#2563EB]" /><span className="text-sm font-bold">Subreddits</span></div>
                    <span className="text-[10px] text-[#9ca3af]">{subreddits.length}/{subLimit}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {subreddits.map((s, i) => (
                      <span key={i} className="inline-flex items-center gap-0.5 text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#F6F7F9] text-[#0B0F17] border border-[#E6E8EE]">
                        r/{s}<button onClick={() => removeSubreddit(s)} className="hover:bg-red-100 rounded-full p-0.5 ml-0.5"><X className="w-2 h-2" /></button>
                      </span>
                    ))}
                  </div>
                  {subreddits.length < subLimit && (
                    <div className="flex gap-1.5">
                      <Input placeholder="r/subreddit" value={newSubreddit} onChange={(e) => setNewSubreddit(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSubreddit())} className="h-7 text-[11px] rounded-md flex-1" />
                      <Button variant="outline" size="sm" className="h-7 w-7 p-0 rounded-md" onClick={addSubreddit}><Plus className="w-3 h-3" /></Button>
                    </div>
                  )}
                </div>
              </div>

              <Button className="w-full h-11 rounded-xl font-bold text-sm cta-btn shadow-lg shadow-[#FF4500]/20"
                style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}
                onClick={handleConfirm} disabled={confirming} data-testid="onboard-confirm-button">
                {confirming ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting...</> : <>Start Scanning Reddit <Sparkles className="ml-2 w-4 h-4" /></>}
              </Button>
              {scanError && <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{scanError}</div>}
            </Card>
          </motion.div>
        )}

        {/* ── Scanning ── */}
        {step === 'scanning' && (
          <motion.div key="scanning" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="relative w-full max-w-2xl">
            <Card className={CARD_CLASS}>
              <div className="flex items-center justify-center mb-6"><CodyIconBadge size={44} /></div>
              <h2 className="text-xl font-bold text-center mb-1" style={{ fontFamily: "'Space Grotesk'" }}>
                {isDone ? 'All set!' : 'Scanning Reddit'}
              </h2>
              <p className="text-sm text-[#5B667A] text-center mb-6">{isDone ? `Found ${scanData?.leads_count || 0} leads — redirecting...` : 'Finding leads and scoring intent...'}</p>
              <Progress value={scanProgress} className="h-2 mb-5" />
              <div className="space-y-2">
                {SCAN_STEPS.map((s, i) => {
                  const done = i < scanStep || isDone;
                  const active = i === scanStep && !isDone;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] ${done ? 'bg-[#16A34A] text-white' : active ? 'bg-[#FF4500] text-white' : 'bg-[#E6E8EE] text-[#9ca3af]'}`}>
                        {done ? <Check className="w-3 h-3" /> : active ? <Loader2 className="w-3 h-3 animate-spin" /> : i + 1}
                      </div>
                      <span className={`text-xs ${done ? 'text-[#0B0F17] font-medium' : active ? 'text-[#FF4500] font-medium' : 'text-[#9ca3af]'}`}>{s.label}</span>
                    </div>
                  );
                })}
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
